import type { UseMutationOptions, UseMutationResult, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addBasicActivityToTrip, regenerateIndividualItem } from 'tripPlanner/api'
import type { AddBasicActivityToTripError, RegenerateItemAiError, RegenerateItemAiResponse } from 'tripPlanner/api/types'
import { invalidateTrip } from './common'

export type RegenerateIndividualItemActivity = RegenerateItemAiResponse['result']['activities'][number]

export const useRegenerateIndividualItem = (
  { tripId, itemId, freeText, ...options }: {
    tripId: string
    itemId: string
    freeText?: string
  } & UseQueryOptions<
    Array<RegenerateIndividualItemActivity>,
    RegenerateItemAiError
  >,
): UseQueryResult<Array<RegenerateIndividualItemActivity>, RegenerateItemAiError> => {
  return useQuery({
    queryKey: ['regenerateIndividualItem', tripId, itemId, freeText],
    queryFn: async() => {
      return regenerateIndividualItem({ itemId, freeText }, tripId)
    },
    ...options,
  })
}

export const useRegenerateIndividualItemMutation = (
  options?: UseMutationOptions<
    Array<RegenerateIndividualItemActivity>,
    RegenerateItemAiError,
    Parameters<typeof regenerateIndividualItem>[0] & { tripId: string }
  >,
): UseMutationResult<
  Array<RegenerateIndividualItemActivity>,
  RegenerateItemAiError,
  Parameters<typeof regenerateIndividualItem>[0] & { tripId: string }
> => {
  return useMutation({
    mutationFn: async(params) => {
      return regenerateIndividualItem({
        itemId: params.itemId,
        freeText: params.freeText,
      }, params.tripId)
    },
    ...options,
  })
}

export const useAddBasicActivityToTripMutation = (
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof addBasicActivityToTrip>>,
    AddBasicActivityToTripError,
    Parameters<typeof addBasicActivityToTrip>[0] & { tripId: string }
  >,
): UseMutationResult<
  Awaited<ReturnType<typeof addBasicActivityToTrip>>,
  AddBasicActivityToTripError,
  Parameters<typeof addBasicActivityToTrip>[0] & { tripId: string }
> => {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options || {}

  return useMutation({
    ...restOptions,
    mutationFn: async(params) => {
      const { tripId, ...activityParams } = params
      return addBasicActivityToTrip(activityParams, tripId)
    },
    onSuccess: (data, variables, context) => {
      invalidateTrip(queryClient, variables.tripId)
      onSuccess?.(data, variables, context)
    },
  })
}
